// Phase 26B · Travel Policy Groups admin master page.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, ShieldCheck, Bus, Ban, Wallet, Moon, Home, BarChart3, Users as UsersIcon } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";

import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const WORKING_TYPES = ["IN_CITY", "OUT_CITY"];

const blankPolicy = () => ({
  code: "",
  name: "",
  description: "",
  is_active: true,
  da_by_working_type: { IN_CITY: 0, OUT_CITY: 0 },
  travel_rates: [{ mode_code: "", rate_per_km_inr: 0, allowed: true }],
  per_claim_type_limits: [],
  monthly_total_cap_inr: 0,
  night_stay: { min_km: 125, amount_inr: 0, enabled: true },
  other_allowances: { lodging_inr_per_day: 0, toll_max_inr: 0, misc_max_inr: 0 },
});

export default function TravelPoliciesPage() {
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission("masters.manage");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [travelModes, setTravelModes] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);

  const [editing, setEditing] = useState(null); // {policy, isNew}
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [auditFor, setAuditFor] = useState(null);   // policy object or null
  const [audit, setAudit] = useState(null);          // response
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditYear, setAuditYear] = useState(new Date().getFullYear());
  const [usersFor, setUsersFor] = useState(null);   // policy object or null
  const [policyUsers, setPolicyUsers] = useState(null); // response items or null (loading)
  const [reassigning, setReassigning] = useState(null); // user_id currently being reassigned

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [pg, tm, ct] = await Promise.all([
        api.get("/travel-policy-groups", { params: { page_size: 100 } }),
        api.get("/lookups", { params: { category: "travel_modes", page_size: 200 } }),
        api.get("/lookups", { params: { category: "other_claim_type", page_size: 200 } }),
      ]);
      setRows(pg.data.items || []);
      setTravelModes((tm.data.items || []).filter(i => i.is_active).map(i => i.code));
      setClaimTypes((ct.data.items || []).filter(i => i.is_active).map(i => i.code));
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => setEditing({ policy: blankPolicy(), isNew: true });
  const openEdit = (p) => setEditing({
    policy: {
      ...blankPolicy(),
      ...p,
      da_by_working_type: { ...blankPolicy().da_by_working_type, ...(p.da_by_working_type || {}) },
      night_stay: { ...blankPolicy().night_stay, ...(p.night_stay || {}) },
      other_allowances: { ...blankPolicy().other_allowances, ...(p.other_allowances || {}) },
      travel_rates: p.travel_rates?.length ? p.travel_rates : blankPolicy().travel_rates,
      per_claim_type_limits: p.per_claim_type_limits || [],
    },
    isNew: false,
  });

  const save = async () => {
    setSaving(true);
    try {
      const p = editing.policy;
      // Trim travel_rates rows with empty mode_code
      const clean = {
        ...p,
        travel_rates: (p.travel_rates || []).filter(r => (r.mode_code || "").trim()),
        per_claim_type_limits: (p.per_claim_type_limits || []).filter(r => (r.claim_type_code || "").trim()),
      };
      if (editing.isNew) {
        await api.post("/travel-policy-groups", clean);
        toast.success(`Policy '${clean.code}' created`);
      } else {
        const { code, ...patch } = clean; // code is not patchable
        await api.patch(`/travel-policy-groups/${p.id}`, patch);
        toast.success(`Policy '${p.code}' updated`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/travel-policy-groups/${confirmDelete.id}`);
      toast.success(`Policy '${confirmDelete.code}' deleted`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openAudit = (p) => {
    setAuditFor(p); setAudit(null); setAuditLoading(true);
    api.get(`/travel-policy-groups/${p.id}/audit`, { params: { year: auditYear } })
      .then(r => setAudit(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setAuditLoading(false));
  };
  const refreshAudit = () => {
    if (!auditFor) return;
    setAuditLoading(true);
    api.get(`/travel-policy-groups/${auditFor.id}/audit`, { params: { year: auditYear } })
      .then(r => setAudit(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setAuditLoading(false));
  };

  const openUsers = (p) => {
    setUsersFor(p); setPolicyUsers(null);
    api.get(`/travel-policy-groups/${p.id}/users`)
      .then(r => setPolicyUsers(r.data.items || []))
      .catch(e => { toast.error(formatApiError(e)); setPolicyUsers([]); });
  };

  // Move a user off this policy — either onto another named group, or back
  // to the legacy singleton config (policy_group_id: null, "Remove").
  // Reuses the existing generic PATCH /users/{id}, which already accepts
  // and validates policy_group_id — no new user-facing endpoint needed.
  const reassignUser = async (userId, newPolicyGroupId) => {
    setReassigning(userId);
    try {
      await api.patch(`/users/${userId}`, { policy_group_id: newPolicyGroupId });
      toast.success(newPolicyGroupId ? "User reassigned" : "User removed from policy");
      setPolicyUsers((list) => (list || []).filter((u) => u.id !== userId));
      await load(); // refresh assigned_user_count badges on the table
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReassigning(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-travel-policies-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Travel Policies" }]}
        title="Travel Policies"
        subtitle="Bundle DA, travel rates, per-claim caps, monthly caps and night-stay rules into named policy groups you can assign to users."
        favorite={{ key: "admin.travel-policies", type: "screen", label: "Travel Policies", route: "/admin/travel-policies" }}
        testid="travel-policies-page-header"
        actions={canManage ? (
          <Button
            data-testid="travel-policies-new"
            onClick={openCreate}
            className="tulsi-gradient text-white h-10 font-semibold rounded-xl shadow-md shadow-[#E5197F]/20"
          >
            <Plus className="h-4 w-4 mr-2" /> New policy
          </Button>
        ) : null}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Policy groups</CardTitle>
          <CardDescription>Users assigned to inactive policies fall back to the legacy singleton config.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-neutral-500 py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="text-sm text-red-600" data-testid="travel-policies-error">{error}</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-neutral-500 py-4">No policy groups yet. Click <b>New policy</b>.</div>
          ) : (
            <Table data-testid="travel-policies-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">DA IN_CITY</TableHead>
                  <TableHead className="text-right">DA OUT_CITY</TableHead>
                  <TableHead className="text-right">Monthly cap</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} data-testid={`travel-policy-row-${r.code}`}>
                    <TableCell className="font-mono text-xs">{r.code}</TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-right">₹{(r.da_by_working_type?.IN_CITY || 0).toFixed(0)}</TableCell>
                    <TableCell className="text-right">₹{(r.da_by_working_type?.OUT_CITY || 0).toFixed(0)}</TableCell>
                    <TableCell className="text-right">
                      {r.monthly_total_cap_inr > 0 ? `₹${r.monthly_total_cap_inr.toFixed(0)}` : <span className="text-neutral-400">—</span>}
                    </TableCell>
                    <TableCell>
                      {r.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canManage ? (
                        <button onClick={() => openUsers(r)}
                          data-testid={`travel-policy-users-${r.code}`}
                          title="View / reassign users on this policy">
                          <Badge variant="outline" className="cursor-pointer hover:bg-neutral-100">
                            {r.assigned_user_count}
                          </Badge>
                        </button>
                      ) : (
                        <Badge variant="outline">{r.assigned_user_count}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canManage && (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openAudit(r)}
                            data-testid={`travel-policy-audit-${r.code}`}
                            title="Policy audit">
                            <BarChart3 className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                            data-testid={`travel-policy-edit-${r.code}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {r.code !== "DEFAULT" && (
                            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}
                              data-testid={`travel-policy-delete-${r.code}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit / Create Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl" data-testid="travel-policy-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? "Create policy group" : `Edit — ${editing?.policy?.code}`}</DialogTitle>
            <DialogDescription>
              Values you set here override the legacy DA/travel-rate tables for any user assigned to this group.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <PolicyForm
              value={editing.policy}
              isNew={editing.isNew}
              onChange={(policy) => setEditing({ ...editing, policy })}
              travelModes={travelModes}
              claimTypes={claimTypes}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="travel-policy-save"
              className="tulsi-gradient text-white font-semibold">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing?.isNew ? "Create" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="travel-policy-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete policy '{confirmDelete?.code}'?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Users assigned to this policy must be reassigned first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Policy Audit Dialog (Phase 26B.1) */}
      <Dialog open={!!auditFor} onOpenChange={(o) => { if (!o) { setAuditFor(null); setAudit(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="travel-policy-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#6D2C91]" /> Policy audit — {auditFor?.code}
            </DialogTitle>
            <DialogDescription>
              Approved vs. rejected claims across users assigned to this policy, plus the top rejection reasons.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-3">
            <Label className="text-xs">Year</Label>
            <Input type="number" min="2020" max="2100" step="1" value={auditYear}
              onChange={(e) => setAuditYear(Number(e.target.value) || auditYear)}
              className="h-8 w-24" data-testid="audit-year-input" />
            <Button size="sm" variant="outline" onClick={refreshAudit} data-testid="audit-refresh">
              {auditLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Refresh"}
            </Button>
          </div>
          {auditLoading ? (
            <div className="flex items-center gap-2 text-sm text-neutral-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading audit…
            </div>
          ) : audit ? (
            <div className="space-y-4">
              {/* Tiles */}
              <div className="grid grid-cols-4 gap-2" data-testid="audit-tiles">
                <Tile label="Users on policy" value={audit.assigned_user_count} />
                <Tile label="Approved" value={audit.approved_count} tone="emerald" />
                <Tile label="Rejected" value={audit.rejected_count} tone="red" />
                <Tile label="Approval rate" value={`${audit.approval_rate_pct}%`} tone="purple" />
              </div>
              <div className="text-[11px] text-neutral-500">
                {audit.date_from} → {audit.date_to} · {audit.total_count} claims
              </div>

              {/* Top rejection reasons — CSS bar chart */}
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-400 mb-2">Top rejection reasons</div>
                {audit.top_rejection_reasons.length === 0 ? (
                  <div className="text-sm text-neutral-500 py-3 border border-dashed border-neutral-200 rounded-lg text-center" data-testid="audit-empty-reasons">
                    No rejections in this window. 
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="audit-reasons-chart">
                    {(() => {
                      const max = Math.max(...audit.top_rejection_reasons.map(r => r.count));
                      return audit.top_rejection_reasons.map((r, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`audit-reason-${i}`}>
                          <div className="text-xs text-neutral-700 w-40 truncate">{r.bucket}</div>
                          <div className="flex-1 h-4 bg-neutral-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#6D2C91] to-[#E5197F] transition-all"
                              style={{ width: `${max ? (r.count / max) * 100 : 0}%` }} />
                          </div>
                          <div className="text-xs font-semibold w-8 text-right">{r.count}</div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-neutral-500 py-4">No audit data.</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAuditFor(null); setAudit(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Users on this policy — view + reassign/remove */}
      <Dialog open={!!usersFor} onOpenChange={(o) => { if (!o) { setUsersFor(null); setPolicyUsers(null); } }}>
        <DialogContent className="max-w-lg" data-testid="travel-policy-users-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-[#6D2C91]" /> Users on {usersFor?.code}
            </DialogTitle>
            <DialogDescription>
              Reassign a user to a different policy, or remove them (falls back to the legacy config).
            </DialogDescription>
          </DialogHeader>
          {policyUsers === null ? (
            <div className="flex items-center gap-2 text-sm text-neutral-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading users…
            </div>
          ) : policyUsers.length === 0 ? (
            <div className="text-sm text-neutral-500 py-4" data-testid="travel-policy-users-empty">
              No users assigned to this policy.
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="travel-policy-users-list">
              {policyUsers.map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 p-2"
                  data-testid={`travel-policy-user-row-${u.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-[11px] text-neutral-500 truncate">
                      {u.email || u.phone} · {u.role_code}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Select
                      disabled={reassigning === u.id}
                      onValueChange={(v) => reassignUser(u.id, v === "__none__" ? null : v)}
                    >
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid={`travel-policy-user-reassign-${u.id}`}>
                        <SelectValue placeholder="Move to…" />
                      </SelectTrigger>
                      <SelectContent>
                        {rows.filter((r) => r.id !== usersFor?.id).map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.code}</SelectItem>
                        ))}
                        <SelectItem value="__none__">Remove (legacy)</SelectItem>
                      </SelectContent>
                    </Select>
                    {reassigning === u.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUsersFor(null); setPolicyUsers(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function Tile({ label, value, tone }) {
  const toneCls = {
    emerald: "text-emerald-700 border-emerald-200 bg-emerald-50",
    red: "text-red-700 border-red-200 bg-red-50",
    purple: "text-[#6D2C91] border-[#6D2C91]/20 bg-[#6D2C91]/5",
  }[tone] || "text-neutral-700 border-neutral-200 bg-white";
  const slug = label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className={`rounded-xl border p-3 ${toneCls}`}
         data-testid={`audit-tile-${slug}`}
         data-tile-slug={slug}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-xl font-extrabold mt-1" data-testid={slug}>{value}</div>
    </div>
  );
}

// ---------------- PolicyForm ------------------
function PolicyForm({ value, isNew, onChange, travelModes, claimTypes }) {
  const upd = (k, v) => onChange({ ...value, [k]: v });
  const updNested = (parent, k, v) => onChange({ ...value, [parent]: { ...value[parent], [k]: v } });

  const updRate = (idx, key, val) => {
    const next = [...(value.travel_rates || [])];
    next[idx] = { ...next[idx], [key]: val };
    upd("travel_rates", next);
  };
  const addRate = () => upd("travel_rates", [...(value.travel_rates || []), { mode_code: "", rate_per_km_inr: 0, allowed: true }]);
  const rmRate = (idx) => upd("travel_rates", value.travel_rates.filter((_, i) => i !== idx));

  const updLimit = (idx, key, val) => {
    const next = [...(value.per_claim_type_limits || [])];
    next[idx] = { ...next[idx], [key]: val };
    upd("per_claim_type_limits", next);
  };
  const addLimit = () => upd("per_claim_type_limits", [...(value.per_claim_type_limits || []), { claim_type_code: "", max_amount_inr: 0 }]);
  const rmLimit = (idx) => upd("per_claim_type_limits", value.per_claim_type_limits.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      {/* Basic */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="pg-code">Code</Label>
          <Input id="pg-code" value={value.code} onChange={(e) => upd("code", e.target.value.toUpperCase())}
            placeholder="STRICT" disabled={!isNew} data-testid="pg-code" />
          <p className="text-[11px] text-neutral-400 mt-1">UPPER_SNAKE_CASE, unique. Cannot be changed after create.</p>
        </div>
        <div>
          <Label htmlFor="pg-name">Name</Label>
          <Input id="pg-name" value={value.name} onChange={(e) => upd("name", e.target.value)}
            placeholder="Strict travel policy" data-testid="pg-name" />
        </div>
        <div className="col-span-2">
          <Label htmlFor="pg-desc">Description</Label>
          <Textarea id="pg-desc" rows={2} value={value.description || ""} onChange={(e) => upd("description", e.target.value)} />
        </div>
        <div className="col-span-2 flex items-center gap-3">
          <Switch checked={value.is_active} onCheckedChange={(v) => upd("is_active", v)} id="pg-active"
            data-testid="pg-active" />
          <Label htmlFor="pg-active">Active</Label>
        </div>
      </div>

      <Tabs defaultValue="da">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="da" data-testid="tab-da"><ShieldCheck className="h-4 w-4 mr-1" />DA</TabsTrigger>
          <TabsTrigger value="travel" data-testid="tab-travel"><Bus className="h-4 w-4 mr-1" />Travel</TabsTrigger>
          <TabsTrigger value="limits" data-testid="tab-limits"><Wallet className="h-4 w-4 mr-1" />Limits</TabsTrigger>
          <TabsTrigger value="night" data-testid="tab-night"><Moon className="h-4 w-4 mr-1" />Night stay</TabsTrigger>
          <TabsTrigger value="other" data-testid="tab-other"><Home className="h-4 w-4 mr-1" />Allowances</TabsTrigger>
        </TabsList>

        <TabsContent value="da" className="space-y-2 pt-3">
          <p className="text-xs text-neutral-500">Auto-added on claim submit based on the user's attendance working type for that day.</p>
          <div className="grid grid-cols-2 gap-3">
            {WORKING_TYPES.map(wt => (
              <div key={wt}>
                <Label htmlFor={`da-${wt}`}>DA — {wt.replace("_", " ")} (₹)</Label>
                <Input id={`da-${wt}`} type="number" min="0" step="1"
                  value={value.da_by_working_type?.[wt] ?? 0}
                  onChange={(e) => updNested("da_by_working_type", wt, Number(e.target.value) || 0)}
                  data-testid={`pg-da-${wt.toLowerCase()}`} />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="travel" className="pt-3">
          <div className="flex justify-between items-center mb-2">
            <div>
              <p className="text-sm font-medium">Travel rates per mode</p>
              <p className="text-xs text-neutral-500">Toggle 'Allowed' to reject a mode outright at submit time.</p>
            </div>
            <Button size="sm" variant="outline" onClick={addRate} data-testid="pg-add-rate">
              <Plus className="h-3 w-3 mr-1" /> Add mode
            </Button>
          </div>
          <div className="space-y-2">
            {(value.travel_rates || []).map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`pg-rate-row-${i}`}>
                <div className="col-span-5">
                  <Label className="text-xs">Mode code</Label>
                  <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    value={r.mode_code || ""}
                    onChange={(e) => updRate(i, "mode_code", e.target.value)}
                    data-testid={`pg-rate-mode-${i}`}>
                    <option value="">Pick a mode…</option>
                    {travelModes.map(m => <option key={m} value={m}>{m}</option>)}
                    {r.mode_code && !travelModes.includes(r.mode_code) && (
                      <option value={r.mode_code}>{r.mode_code} (legacy)</option>
                    )}
                  </select>
                </div>
                <div className="col-span-4">
                  <Label className="text-xs">Rate ₹/km</Label>
                  <Input type="number" min="0" step="0.5" value={r.rate_per_km_inr}
                    onChange={(e) => updRate(i, "rate_per_km_inr", Number(e.target.value) || 0)}
                    data-testid={`pg-rate-value-${i}`} />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Switch checked={r.allowed} onCheckedChange={(v) => updRate(i, "allowed", v)}
                    data-testid={`pg-rate-allowed-${i}`} />
                  <span className="text-xs">{r.allowed ? "Allowed" : <span className="text-red-600 flex items-center gap-1"><Ban className="h-3 w-3" />Blocked</span>}</span>
                </div>
                <div className="col-span-1">
                  <Button size="sm" variant="ghost" onClick={() => rmRate(i)} className="text-red-600 hover:bg-red-50"
                    data-testid={`pg-rate-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="limits" className="pt-3 space-y-3">
          <div>
            <Label>Monthly total cap (₹)</Label>
            <Input type="number" min="0" step="100" value={value.monthly_total_cap_inr || 0}
              onChange={(e) => upd("monthly_total_cap_inr", Number(e.target.value) || 0)}
              data-testid="pg-monthly-cap" />
            <p className="text-[11px] text-neutral-400 mt-1">Set to 0 to disable the monthly cap.</p>
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <div>
                <p className="text-sm font-medium">Per-claim-type limits</p>
                <p className="text-xs text-neutral-500">Any 'other' item that exceeds its limit is rejected at submit.</p>
              </div>
              <Button size="sm" variant="outline" onClick={addLimit} data-testid="pg-add-limit">
                <Plus className="h-3 w-3 mr-1" /> Add limit
              </Button>
            </div>
            <div className="space-y-2">
              {(value.per_claim_type_limits || []).map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`pg-limit-row-${i}`}>
                  <div className="col-span-7">
                    <Label className="text-xs">Claim type code</Label>
                    <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      value={r.claim_type_code || ""}
                      onChange={(e) => updLimit(i, "claim_type_code", e.target.value)}
                      data-testid={`pg-limit-type-${i}`}>
                      <option value="">Pick…</option>
                      {claimTypes.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Max ₹</Label>
                    <Input type="number" min="0" step="10" value={r.max_amount_inr}
                      onChange={(e) => updLimit(i, "max_amount_inr", Number(e.target.value) || 0)}
                      data-testid={`pg-limit-value-${i}`} />
                  </div>
                  <div className="col-span-1">
                    <Button size="sm" variant="ghost" onClick={() => rmLimit(i)} className="text-red-600 hover:bg-red-50"
                      data-testid={`pg-limit-remove-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="night" className="pt-3 space-y-3">
          <div className="flex items-center gap-3">
            <Switch checked={value.night_stay?.enabled}
              onCheckedChange={(v) => updNested("night_stay", "enabled", v)}
              data-testid="pg-night-enabled" />
            <Label>Enable night-stay validation</Label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Minimum kilometres</Label>
              <Input type="number" min="0" step="1" value={value.night_stay?.min_km ?? 0}
                onChange={(e) => updNested("night_stay", "min_km", Number(e.target.value) || 0)}
                data-testid="pg-night-min-km" />
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" min="0" step="10" value={value.night_stay?.amount_inr ?? 0}
                onChange={(e) => updNested("night_stay", "amount_inr", Number(e.target.value) || 0)}
                data-testid="pg-night-amount" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="other" className="pt-3 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Lodging (₹/day)</Label>
              <Input type="number" min="0" step="50" value={value.other_allowances?.lodging_inr_per_day ?? 0}
                onChange={(e) => updNested("other_allowances", "lodging_inr_per_day", Number(e.target.value) || 0)}
                data-testid="pg-oa-lodging" />
            </div>
            <div>
              <Label>Toll cap (₹)</Label>
              <Input type="number" min="0" step="10" value={value.other_allowances?.toll_max_inr ?? 0}
                onChange={(e) => updNested("other_allowances", "toll_max_inr", Number(e.target.value) || 0)}
                data-testid="pg-oa-toll" />
            </div>
            <div>
              <Label>Misc cap (₹)</Label>
              <Input type="number" min="0" step="10" value={value.other_allowances?.misc_max_inr ?? 0}
                onChange={(e) => updNested("other_allowances", "misc_max_inr", Number(e.target.value) || 0)}
                data-testid="pg-oa-misc" />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
